import { CompetitorDropdown } from "components/ui/dropdown/CompetitorDropdown/CompetitorDropdown"
import type { ColumnsType } from "antd/es/table"
import type { MenuProps } from "antd"
import style from "./CompetitorCategoryTable.module.scss"

const { cellContent, subcategoryHeaderCell, editButton, columnHeader } = style

export type TCompetitorInfo = {
	competitor_id: number
	competitor_name: string
	competitor_model_numbers: string[]
}

export type TStevensModelData = {
	stevens_id: number
	stevens_model_number: string
	competitors: TCompetitorInfo[]
}

export type TSubCategory = {
	[subcategoryName: string]: TStevensModelData[]
}

export type TTopLevelCategory = {
	[topLevelCategoryName: string]: TSubCategory
}

export type TTableRowData = {
	key: string
	isSubcategoryHeader?: boolean
	subcategoryName?: string
	stevens_model_number?: string
	stevens_id?: number
	[competitorName: string]: string | boolean | number | undefined
}

export type TCompetitor = { id: number; name: string }

type BuilderDeps = {
	competitors?: TCompetitor[]
	getCompetitorDropdownItems: (competitorId: number, competitorName: string) => MenuProps["items"]
	getStevensModelDropdownItems: (stevensId: number, stevensModelNumber: string) => MenuProps["items"]
	onEditCompetitorModelNumbers: (
		competitorId: number,
		competitorName: string,
		stevensId: number,
		stevensModelNumber: string,
		modelNumbers: string[],
	) => void
}

// Hoisted so onCell hands back the same object for every cell instead of allocating
// one per cell per render.
const EMPTY_CELL_PROPS = {}
const HIDDEN_CELL_PROPS = { colSpan: 0 }

export const STEVENS_COLUMN_WIDTH = 200
export const COMPETITOR_COLUMN_WIDTH = 220

export type TCompetitorsTableBuilder = (subCategories: TSubCategory) => {
	columns: ColumnsType<TTableRowData>
	data: TTableRowData[]
	/** Total column width — antd's virtual mode needs a numeric scroll.x. */
	totalWidth: number
}

/**
 * Creates the builder that turns one category's data into antd columns + rows.
 *
 * Returned as a factory so the caller can memoize it on its dependencies: the
 * builder must stay referentially stable, otherwise CompetitorCategoryTable
 * rebuilds every row on each render.
 */
export const createCompetitorsTableBuilder = ({
	competitors,
	getCompetitorDropdownItems,
	getStevensModelDropdownItems,
	onEditCompetitorModelNumbers,
}: BuilderDeps): TCompetitorsTableBuilder => {
	return (subCategories: TSubCategory) => {
		// Use competitors from the separate endpoint
		const uniqueCompetitors = competitors?.map((comp) => comp.name) || []
		const competitorsMap = new Map<string, number>(competitors?.map((comp) => [comp.name, comp.id]) || [])

		// Build table data with subcategory headers and model rows
		const tableData: TTableRowData[] = []

		Object.entries(subCategories).forEach(([subcategoryName, items]) => {
			// Add subcategory header row
			tableData.push({
				key: `header-${subcategoryName}`,
				isSubcategoryHeader: true,
				subcategoryName: subcategoryName,
			})

			// Add model number rows for this subcategory
			items.forEach((item) => {
				const competitorMap = new Map<string, string[]>()
				item.competitors.forEach((comp) => {
					competitorMap.set(comp.competitor_name, comp.competitor_model_numbers)
				})

				const rowData: TTableRowData = {
					key: `model-${item.stevens_id}`,
					isSubcategoryHeader: false,
					stevens_model_number: item.stevens_model_number,
					stevens_id: item.stevens_id,
				}

				// Add competitor columns
				uniqueCompetitors.forEach((competitorName: string) => {
					const modelNumbers = competitorMap.get(competitorName) || []
					rowData[competitorName] = modelNumbers.join(", ")
				})

				tableData.push(rowData)
			})
		})

		const subcategoryHeaderCellProps = {
			colSpan: uniqueCompetitors.length + 1,
			className: subcategoryHeaderCell,
		}

		// Build columns
		const columns: ColumnsType<TTableRowData> = [
			{
				title: "STEVENS",
				dataIndex: "stevens_model_number",
				key: "stevens",
				width: STEVENS_COLUMN_WIDTH,
				onCell: (record) => (record.isSubcategoryHeader ? subcategoryHeaderCellProps : EMPTY_CELL_PROPS),
				render: (text, record) => {
					if (record.isSubcategoryHeader) {
						return record.subcategoryName
					}

					return (
						<div className={cellContent}>
							<span>{text}</span>
							{record.stevens_id && (
								// `lazy` + an items factory: one antd Dropdown per row is the single
								// most expensive thing in this table, so it is only built on click.
								<CompetitorDropdown
									lazy
									items={() => getStevensModelDropdownItems(record.stevens_id as number, text)}
								/>
							)}
						</div>
					)
				},
			},
			...uniqueCompetitors.map((competitorName: string) => {
				const competitorId = competitorsMap.get(competitorName)!
				return {
					title: (
						<div className={columnHeader}>
							<span>{competitorName}</span>
							<CompetitorDropdown items={getCompetitorDropdownItems(competitorId, competitorName)} />
						</div>
					),
					dataIndex: competitorName,
					key: competitorName,
					width: COMPETITOR_COLUMN_WIDTH,
					onCell: (record: TTableRowData) =>
						record.isSubcategoryHeader ? HIDDEN_CELL_PROPS : (EMPTY_CELL_PROPS as object),
					render: (text: string, record: TTableRowData) => {
						if (record.isSubcategoryHeader) {
							return null
						}

						// Parse model numbers from text (they are joined with ", ")
						const modelNumbers = text ? text.split(", ").filter(Boolean) : []

						return (
							<div className={cellContent}>
								<span>{text || ""}</span>
								<button
									className={editButton}
									onClick={() =>
										onEditCompetitorModelNumbers(
											competitorId,
											competitorName,
											record.stevens_id || 0,
											record.stevens_model_number || "",
											modelNumbers,
										)
									}
									aria-label={`Edit ${competitorName} model numbers for ${record.stevens_model_number}`}
								/>
							</div>
						)
					},
				}
			}),
		]

		return {
			columns,
			data: tableData,
			totalWidth: STEVENS_COLUMN_WIDTH + uniqueCompetitors.length * COMPETITOR_COLUMN_WIDTH,
		}
	}
}
